import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["childFieldsList", "addFieldSelect", "childFieldsData",
                     "minEntries", "maxEntries", "emptyMessage"]

  connect() {
    this.renderChildFields()
    this.updateHiddenData()
  }

  get childFieldsValue() {
    return this.parseArrayValue(this.data.get("childFieldsValue"))
  }

  set childFieldsValue(value) {
    this.data.set("childFieldsValue", JSON.stringify(Array.isArray(value) ? value : []))
  }

  addField() {
    const select = this.addFieldSelectTarget
    const fieldId = parseInt(select.value)
    if (!fieldId) return

    const option = select.options[select.selectedIndex]
    const fieldName = option.text

    // Check if already added
    if (this.childFieldsValue.find(cf => cf.input_field_id === fieldId)) {
      return
    }

    const updated = [...this.childFieldsValue, {
      input_field_id: fieldId,
      name: fieldName,
      required: false
    }]
    this.childFieldsValue = updated

    // Reset select
    select.value = ""
    this.renderChildFields()
    this.updateHiddenData()
  }

  removeField(event) {
    const fieldId = parseInt(event.currentTarget.dataset.fieldId)
    this.childFieldsValue = this.childFieldsValue.filter(cf => cf.input_field_id !== fieldId)
    this.renderChildFields()
    this.updateHiddenData()
  }

  toggleRequired(event) {
    const fieldId = parseInt(event.currentTarget.dataset.fieldId)
    this.childFieldsValue = this.childFieldsValue.map(cf => {
      if (cf.input_field_id === fieldId) {
        return { ...cf, required: event.currentTarget.checked }
      }
      return cf
    })
    this.updateHiddenData()
  }

  moveUp(event) {
    const fieldId = parseInt(event.currentTarget.dataset.fieldId)
    const index = this.childFieldsValue.findIndex(cf => cf.input_field_id === fieldId)
    if (index <= 0) return

    const updated = [...this.childFieldsValue]
    const temp = updated[index - 1]
    updated[index - 1] = updated[index]
    updated[index] = temp
    this.childFieldsValue = updated
    this.renderChildFields()
    this.updateHiddenData()
  }

  moveDown(event) {
    const fieldId = parseInt(event.currentTarget.dataset.fieldId)
    const index = this.childFieldsValue.findIndex(cf => cf.input_field_id === fieldId)
    if (index < 0 || index >= this.childFieldsValue.length - 1) return

    const updated = [...this.childFieldsValue]
    const temp = updated[index + 1]
    updated[index + 1] = updated[index]
    updated[index] = temp
    this.childFieldsValue = updated
    this.renderChildFields()
    this.updateHiddenData()
  }

  updateMinEntries() {
    this.updateHiddenData()
  }

  updateMaxEntries() {
    this.updateHiddenData()
  }

  renderChildFields() {
    const list = this.childFieldsListTarget
    list.innerHTML = ""

    if (this.childFieldsValue.length === 0) {
      if (this.hasEmptyMessageTarget) {
        this.emptyMessageTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasEmptyMessageTarget) {
      this.emptyMessageTarget.classList.add("hidden")
    }

    this.childFieldsValue.forEach((field, index) => {
      const row = document.createElement("div")
      row.className = "flex items-center gap-2 py-2 px-3 bg-white border border-gray-200 rounded-md mb-2"

      row.innerHTML = `
        <span class="text-sm text-gray-500 w-6">${index + 1}.</span>
        <span class="flex-1 text-sm font-medium">${this.escapeHtml(field.name || "Field #" + field.input_field_id)}</span>
        <label class="flex items-center gap-1 text-xs text-gray-600">
          <input type="checkbox" ${field.required ? "checked" : ""}
                 data-field-id="${field.input_field_id}"
                 data-action="change->fieldset-config#toggleRequired"
                 class="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600">
          Required
        </label>
        <button type="button" data-field-id="${field.input_field_id}"
                data-action="click->fieldset-config#moveUp"
                class="text-gray-400 hover:text-gray-600 text-xs px-1" title="Move up">&#9650;</button>
        <button type="button" data-field-id="${field.input_field_id}"
                data-action="click->fieldset-config#moveDown"
                class="text-gray-400 hover:text-gray-600 text-xs px-1" title="Move down">&#9660;</button>
        <button type="button" data-field-id="${field.input_field_id}"
                data-action="click->fieldset-config#removeField"
                class="text-red-400 hover:text-red-600 text-xs px-1" title="Remove">&times;</button>
      `
      list.appendChild(row)
    })
  }

  updateHiddenData() {
    const dataForSubmit = this.childFieldsValue.map(cf => ({
      input_field_id: cf.input_field_id,
      required: cf.required
    }))

    const payload = {
      child_fields: dataForSubmit,
      min_entries: isNaN(parseInt(this.minEntriesTarget.value)) ? 1 : parseInt(this.minEntriesTarget.value),
      max_entries: this.maxEntriesTarget.value ? parseInt(this.maxEntriesTarget.value) : null
    }

    this.childFieldsDataTarget.value = JSON.stringify(payload)
  }

  parseArrayValue(value) {
    if (!value) return []

    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch (error) {
      return []
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
